import { IonButton, IonIcon, IonItem, IonLabel } from '@ionic/react';
import {
  MutationWithContext,
  retryFailedMutation,
} from '@lidea/shared/data-access/core';
import { Collapse, Group, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  checkmarkCircle,
  closeCircle,
  pauseCircle,
  refreshCircle,
  reload,
  trashBin,
} from 'ionicons/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ClearMutationsButton } from '../clear-mutations-button/clear-mutations-button';
import { MutationPreview } from '../mutation-preview/mutation-preview';

interface MutationItemProps {
  status: 'pending' | 'error' | 'success' | 'paused';
  mutation: MutationWithContext;
  fieldId: number;
}

export const MutationItem: React.FC<MutationItemProps> = ({
  mutation,
  status: state,
  fieldId,
}) => {
  const { t } = useTranslation(['common'], { useSuspense: false });
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const color =
    state === 'error'
      ? 'danger'
      : state === 'success'
      ? 'success'
      : state === 'pending'
      ? 'warning'
      : 'purple';

  const icon =
    state === 'error'
      ? closeCircle
      : state === 'success'
      ? checkmarkCircle
      : state === 'pending'
      ? refreshCircle
      : pauseCircle;

  return (
    <>
      <IonItem
        button={state === 'error'}
        color="light"
        detail={state === 'error'}
        lines="full"
        onClick={() => {
          if (state === 'error') {
            setExpanded((prev) => !prev);
          }
        }}
      >
        <IonIcon
          color={color}
          icon={icon}
          style={{ marginRight: '8px', flexShrink: 0 }}
        ></IonIcon>
        <IonLabel>
          <Text fw="bold" lh={1.2} lineClamp={1} truncate>
            {mutation.state.context?.metadata.title}
          </Text>
          <Text lh={1} lineClamp={1} size="sm" truncate>
            {dayjs(mutation.state.submittedAt).format(
              'ddd DD MMM YYYY HH[H]mm'
            )}
          </Text>
        </IonLabel>
      </IonItem>

      <Collapse in={expanded}>
        {expanded ? (
          <MutationPreview fieldId={fieldId} mutation={mutation} />
        ) : null}
        <Group p="sm" position="apart" pt={0}>
          <ClearMutationsButton id={mutation.mutationId}>
            {t('common:delete')}
            <IonIcon icon={trashBin} size="small" slot="end"></IonIcon>
          </ClearMutationsButton>
          <IonButton
            size="small"
            onClick={() => {
              retryFailedMutation(queryClient, mutation.mutationId);
            }}
          >
            {t('common:retry')}
            <IonIcon icon={reload} size="small" slot="end"></IonIcon>
          </IonButton>
        </Group>
      </Collapse>
    </>
  );
};
